import { ControllerInterface } from "csi/action/Controller";
import CsiFormData from "csi/action/FormData";
import comm from "csi/comm";
import JsonResponse from "csi/comm/JsonResponse";
import ExtendedPath from "csi/core/ExtendedPath";
import Spec from "csi/core/Spec";
import NodeDataStore from "csi/datastore/NodeDataStore";
import def from "csi/def";
import { FormWidget } from "csi/form/Widget";
import { applyRuleSpecToQuery, getKeyMapAndRules, RuntimeRulesSchema, updateRules } from "csi/middleware/attributeRule/RuleService";
import RuleStore from "csi/middleware/attributeRule/RuleStore";
import AttributeSettingStore from "csi/middleware/AttributeSettingStore";
import { ControllerSaveFunction, finalizeReactNewRequest, removeDuplicatedSpecs, storeFormDataFieldValue } from "csi/middleware/common";
import { convertRuntimeRulesSchemaToCustomFilters } from "csi/middleware/customView/customViewDataManagementService";
import SearchManager from "csi/page/SearchManager";
import { getSearch } from "csi/service/dataStoreService";
import { submitWriter } from "csi/service/writerService";
import { maxResultSize } from "csi/sessionConstants";
import rootStore from "csi/state/rootStore";
import WorkingCache from "csi/state/WorkingCache";
import { NewInterface } from "csi/table/NewInterface";
import { C8Node, cnl, ICache } from "csi/typings";
import { PATH_OP_PARENT, pathCreate } from "csi/utility/pathUtility";
import csiView from "csi/view";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Search from "csi/xml/Search";
import Writer from "csi/xml/Writer";
import WizardStore from "designSystem/components/wizard/WizardStore";
import { ILocaleProvider } from "designSystem/locale";
import _ from "lodash";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { createContext, useContext } from "react";
import {
	EXCLUDE_ITEM_IDS,
	excludeSpecialStyles,
	getAdHocSpecs,
	getAttributeRuleSpecsForType,
	getColorMatrixPath,
	getProductDependencyPaths,
	onApplyPresentationProductFilter,
	predictProductViolations,
	PRESENTATION_PRODUCTS,
	setAdHocIds,
	STYLE
} from "../PPService";
import ProductGalleryStore from "./ProductGalleryStore";

enum ViewEnum {
	editRuleView = 0,
	violationView = 1,
}

type ActiveView = keyof typeof ViewEnum;

export class PPWizardStore extends WizardStore {
	@observable private _addStyles = true;
	@observable.ref private _futureViolationColorUrls: Set<cnl> = new Set();
	@observable.ref private _futureViolationUrlsOfProductDueToColor: Set<cnl> = new Set();
	@observable.ref private _futureViolationUrlsOfProduct: Set<cnl> = new Set();
	@observable.ref private _futureViolationDataStore: NodeDataStore;
	@observable.ref private _galleryStore: ProductGalleryStore;
	@observable.ref private _ruleStore: RuleStore;
	@observable private _name: string;
	@observable.ref private _newAddedProductUrls: cnl[];
	@observable private _productType: string;
	@observable private readonly _runtimeRules: RuntimeRulesSchema;
	@observable private _specs: Spec[];
	@observable private _activeView: ActiveView = "editRuleView";
	@observable private _viewInfoInViolationPredictionDialog = false;

	private readonly cache: ICache = new WorkingCache();

	/** action controller to access to be able to change writer */
	readonly actionController: ControllerInterface;

	/** FormData for the wizard */
	readonly formData: CsiFormData;

	/** callback for update rule */
	private readonly postUpdateRule: VoidFunction;

	/** url of PP */
	private readonly presentationUrl: cnl;

	/** the active view */
	get activeView(): ActiveView {
		return this._activeView;
	}

	/** adHoc specs from local storage */
	@computed get adHocSpecs(): Spec[] {
		const { primaryType, secondaryType } = this;
		return getAdHocSpecs(primaryType, secondaryType);
	}

	/** the urls of being added products */
	get beingAddedProductUrls(): cnl[] {
		return this._newAddedProductUrls;
	}

	/** item ids already selected  */
	@computed get preSelectIds(): string[] {
		const { specs } = this;

		// TO DO: need to pass the ones which are being referenced by the existing rules
		return specs?.map((spec) => spec.key.toString());
	}

	/** attribute setting store */
	@computed get attributeSettingStore(): AttributeSettingStore {
		const { primaryType, secondaryType, preSelectIds, ruleStore, specs } = this;
		const { setSpecs } = ruleStore || {};

		const setAdHocAndUpdate = action((primaryIds: string[], secondaryIds: string[]) => {
			setAdHocIds([primaryType, primaryIds], [secondaryType, secondaryIds]);
			const { adHocSpecs } = this;

			// force update the rule store
			const finalSpecs = removeDuplicatedSpecs([...specs, ...adHocSpecs]);
			setSpecs?.(finalSpecs);
		});

		return new AttributeSettingStore(preSelectIds, [primaryType, secondaryType], setAdHocAndUpdate, EXCLUDE_ITEM_IDS);
	}

	/** matrix path from product type to its colors */
	@computed get colorMatrixPath(): ExtendedPath | undefined {
		return getColorMatrixPath(this.primaryType);
	}

	/** query for colors according to the rules */
	@computed get colorQuery(): Query {
		const { colorMatrixPath, ruleStore } = this;

		if (!ruleStore) {
			return null;
		}

		const { isAnyCondition, validRules } = ruleStore;
		const q = new Query();
		const q2 = isAnyCondition ? q.Or() : q;

		for (const { ruleSpec } of validRules) {
			applyRuleSpecToQuery(ruleSpec, q2, colorMatrixPath);
		}

		return q2.isEmpty() ? null : q;
	}

	/** indicates if adding styles is disallowed due to number of results matching rules */
	get disableAddStyles(): boolean {
		const { resultCount, resultCountOverLimit } = this;
		return !resultCount || resultCountOverLimit;
	}

	/** store used to query products with violations matching rules */
	@computed get futureGalleryViolationStore(): ProductGalleryStore {
		const {
			futureViolationDataStore,
			presentationUrl,
			showAttributesInDialog,
		} = this;

		return new ProductGalleryStore(
			futureViolationDataStore,
			showAttributesInDialog,
			null,
			presentationUrl,
		);
	}

	/** store for the products with future violations */
	get futureViolationDataStore(): NodeDataStore {
		return this._futureViolationDataStore;
	}

	/** the url set of violations of color */
	get futureViolationColorUrls(): Set<cnl> {
		return this._futureViolationColorUrls;
	}

	/** the urls of violation, including the ones due to both products and colors */
	@computed get futureViolationUrls(): Set<cnl> {
		const { futureViolationUrlsOfProductDueToColor, futureViolationUrlsOfProduct } = this;
		return new Set([...Array.from(futureViolationUrlsOfProductDueToColor), ...Array.from(futureViolationUrlsOfProduct)]);
	}

	/** the url set of violations due to color */
	get futureViolationUrlsOfProductDueToColor(): Set<cnl> {
		return this._futureViolationUrlsOfProductDueToColor;
	}

	/** the url set of violations due to product */
	get futureViolationUrlsOfProduct(): Set<cnl> {
		return this._futureViolationUrlsOfProduct;
	}

	/** store used to query products matching rules */
	get galleryStore(): ProductGalleryStore {
		return this._galleryStore;
	}

	/** indicates if there are any valid rules defined */
	@computed get hasRules(): boolean {
		return this._ruleStore?.hasValidRule;
	}

	/** the name of Product Presentation */
	get name(): string {
		return this._name;
	}

	/** presentation node */
	@computed get presentation(): C8Node | null {
		const { presentationUrl, cache } = this;

		if (!presentationUrl) {
			return null;
		}

		return cache.get(presentationUrl);
	}

	/** primary node type in presentation */
	@computed get primaryType(): string {
		const { presentation, productType } = this;
		return xml.enumGetText(productType || presentation?.ProductType);
	}

	/** dependency paths required to display products */
	@computed get productDependencyPaths(): string[] {
		const validRules = this.ruleStore?.validRules;
		const specs = validRules?.map((rule) => rule.spec);
		return getProductDependencyPaths(true, specs);
	}

	/** query for product request based on rules */
	@computed get productQuery(): Query | null {
		const { hasRules, primaryType, ruleQuery, presentationUrl } = this;

		if (!primaryType || !presentationUrl && !hasRules) {
			return null;
		}

		if (presentationUrl) {
			// in the case of edit rule, need to reverse
			ruleQuery.Not().Node(xml.PROP_URL, xml.OP_EQUALS, presentationUrl, pathCreate(PATH_OP_PARENT, PRESENTATION_PRODUCTS));
		}

		return ruleQuery;
	}

	/** the query based on the rules */
	@computed get ruleQuery(): Query | null {
		const { primaryType, ruleStore } = this;

		if (!ruleStore) {
			return null;
		}

		const { isAnyCondition, validRules } = ruleStore;
		const query = new Query();
		query.Type(primaryType);

		if (STYLE === primaryType) {
			excludeSpecialStyles(query);
		}

		SearchManager.getSearchTypeQuery(primaryType);
		const queryContainer = isAnyCondition ? query.Or() : query;

		for (const { ruleSpec } of validRules) {
			applyRuleSpecToQuery(ruleSpec, queryContainer, null, onApplyPresentationProductFilter);
		}

		return query;
	}

	/** the search to find the valid products */
	@computed get searchForValidityProducts(): Search {
		const { presentationUrl, primaryType, validProductQuery } = this;
		const search = getSearch("BaseProduct.ProductPresentation", presentationUrl, { type: primaryType });
		search.getQuery().importQuery(validProductQuery);

		return search;
	}

	/** return secondary type for given type of product to its colors (if any) */
	@computed get secondaryType(): string {
		return this.colorMatrixPath?.getEndType() || "";
	}

	/** indicate if attributes should be shown for each product in violation view */
	get showAttributesInDialog(): boolean {
		return rootStore.localStore.productPresentation.showAttributesInDialog;
	}

	/** data store to query for products matching rules */
	get productsAreLoading(): boolean {
		return this.productStore?.isLoading;
	}

	/** data store to query for products matching rules */
	get productStore(): NodeDataStore | null {
		return this._galleryStore?.store;
	}

	/** the type of the product */
	get productType(): string {
		return this._productType;
	}

	/** number of styles that match the rules */
	get resultCount(): number {
		return this.productStore?.totalCount || 0;
	}

	/** limit of the number of styles that can be added or displayed */
	get resultCountLimit(): number {
		return maxResultSize;
	}

	/** indicates if adding styles is disallowed because count is over limit */
	get resultCountOverLimit(): boolean {
		return this.resultCount > this.resultCountLimit;
	}

	/** rule store */
	@computed get ruleStore(): RuleStore {
		return this._ruleStore;
	}

	/** url of the rule node */
	get ruleUrl(): cnl {
		return this.runtimeRules.ruleUrl;
	}

	/** run time rules */
	get runtimeRules(): RuntimeRulesSchema | null {
		return this._runtimeRules;
	}

	/** indicates whether or not to add all styles matching rules to presentation */
	get shouldAddStyles(): boolean {
		return this._addStyles && !this.disableAddStyles;
	}

	/** specs admin configures */
	get specs(): Spec[] {
		return this._specs;
	}

	/** 
	 * the query based on the rules
	 *  Note: the same as ruleQuery, but, other conditions would apply to ruleQuery in the case of edit rule, so create a separate query to calculate the valid products
	 */
	@computed get validProductQuery(): Query | null {
		const { primaryType, ruleStore } = this;

		if (!ruleStore) {
			return null;
		}

		const { isAnyCondition, validRules } = ruleStore;
		const query = new Query();
		query.Type(primaryType);
		SearchManager.getSearchTypeQuery(primaryType);

		const queryContainer = isAnyCondition ? query.Or() : query;

		for (const { ruleSpec } of validRules) {
			applyRuleSpecToQuery(ruleSpec, queryContainer, null, onApplyPresentationProductFilter);
		}

		return query;
	}

	/** indicate whether or not show the option */
	get viewInfoInViolationPredictionDialog(): boolean {
		return this._viewInfoInViolationPredictionDialog;
	}

	/** hide "view Info" option  */
	@action disableViewInfoInViolationPredictionDialog = (): void => {
		this._viewInfoInViolationPredictionDialog = false;
	};

	/** show "view Info" option  */
	@action enableViewInfoInViolationPredictionDialog = (): void => {
		this._viewInfoInViolationPredictionDialog = true;
	};

	/** indicate if styles matching rules should be added to presentation */
	@action setAddStyles = (value: boolean): void => {
		this._addStyles = value;
	};

	/** extract information from form */
	protected onNextPage = async (index: number): Promise<boolean> => {
		if (index == 0) {
			const { formData } = this;
			const formWidget = csiView.getView(def.DEFAULT_FORM_ID) as FormWidget;

			if (!formWidget.validateForm({ message: "" })) {
				// TODO - how to render the error message
				return false;
			}

			// store field values as initial values in form data - in case Back button is pressed and need to rebuild form,
			// or in case Finish button is pressed and need to add form values to request
			formData.getFields().forEach((f) => {
				const localizedValue = f.getFormattedValue();
				const isRequired = f.m_bRequired;

				// only add the non empty values of required fields
				if (isRequired && localizedValue) {
					this.stepInfoList[0].values.push(localizedValue);
				}

				// populate the initial value to save the information to node later
				storeFormDataFieldValue(f, formData);
			});

			// initialize the rule store
			this._productType = formData.getFieldValue("ProductType");
		}

		return true;
	};

	/** save PP and rule node */
	protected onSavePromise = async (): Promise<JsonResponse> => {
		const { actionController, ruleStore, productStore, _savePPWithProducts } = this;
		const w = actionController.m_writer;
		const productUrls = await productStore?.resolveUrls();

		const ppUrl = _savePPWithProducts(productUrls);
		const ruleUrl = xml.getUrlPath(ppUrl, xml.path("Rule"));
		updateRules(ruleUrl, w, ruleStore);

		return submitWriter(w);
	};

	/** save presentation with given product urls */
	private _savePPWithProducts = (productUrls?: cnl[]): cnl => {
		const { actionController, shouldAddStyles } = this;
		const ppUrl = comm.createUrl();
		const cn = actionController.m_writer.createNode(ppUrl, "ProductPresentation");

		if (shouldAddStyles && productUrls) {
			cn.changeAttribute(PRESENTATION_PRODUCTS, xml.ATTR_REFLIST, productUrls);
		}

		finalizeReactNewRequest(actionController as NewInterface, ppUrl);

		return ppUrl;
	};

	/** navigate to the violation view */
	@action goToViolationView = (): void => {
		const { setActiveView, initializeViolationStore } = this;

		setActiveView("violationView");
		initializeViolationStore();
	};

	/** navigate to the edit rule view */
	backToEditRuleView = (): void => {
		this.setActiveView("editRuleView");
	};

	/** update the rule node and pp node */
	updateRule = async (): Promise<void> => {
		const { ruleStore, ruleUrl, shouldAddStyles, presentationUrl, presentation, beingAddedProductUrls, searchForValidityProducts, cache, postUpdateRule } = this;

		// extract the key and rules
		const [keyMap, rulesJSON] = getKeyMapAndRules(ruleStore);

		// collect the invalid products
		const responseForValidity = await searchForValidityProducts.execute();

		// exclude the valid ones, then the rest would be invalid ones
		const validProductUrls = responseForValidity.getResultUrls();
		const invalidProductUrls = _.difference(presentation.PPProducts, validProductUrls);

		// update rule node
		const writer = new Writer();

		const call = writer.callMethod("ProductPresentation", "EditPresentationRule")
			.addParameter("PresentationUrl", xml.ATTR_CNL, presentationUrl)
			.addParameter("RuleUrl", xml.ATTR_CNL, ruleUrl)
			.addParameter("Keys", xml.ATTR_STRINGMAP, keyMap)
			.addParameter("Options", xml.ATTR_STRING, rulesJSON);

		//  append new added nodes if any
		if (shouldAddStyles && beingAddedProductUrls.length) {
			call.addParameter("NewAddedProductUrls", xml.ATTR_REFLIST, beingAddedProductUrls);
		}

		if (!_.isEmpty(invalidProductUrls)) {
			call.addParameter("InvalidProductUrls", xml.ATTR_REFLIST, invalidProductUrls);
		}

		const response = await submitWriter(writer);

		// update cache, so that the related components can get updated
		cache.cacheResponse(response);

		// callback
		postUpdateRule();
	};

	/** create rule store */
	@action _primaryTypeReaction = async (): Promise<void> => {
		const { locale, primaryType, adHocSpecs } = this;
		let specs: Spec[];

		if (primaryType) {
			specs = await getAttributeRuleSpecsForType(primaryType);
		}

		runInAction(() => {
			// limit number of rules to 5
			this._specs = specs;

			const finalSpecs = removeDuplicatedSpecs([...specs, ...adHocSpecs]);
			this._ruleStore = new RuleStore(locale, finalSpecs, 5, SearchManager.getSpecAttributeValueStore, null, true);
			if (!!finalSpecs.length) {
				// add one rule by default if the attribute is available
				this._ruleStore.addRule();
			}
		});
	};

	/** create product gallery store */
	@action private _productSearchReaction = async (): Promise<void> => {
		const { cache, colorQuery, productDependencyPaths, productQuery, presentationUrl, presentation, validProductQuery } = this;

		if (productQuery) {
			const search = new Search();
			search.getQuery().importQuery(productQuery);
			search.getSortQuery().SortByNode(xml.PROP_NAME);
			search.addDependencyPaths(productDependencyPaths);

			this._galleryStore = new ProductGalleryStore(new NodeDataStore(search, cache), true, colorQuery);
		} else {
			this._galleryStore = null;
		}

		if (presentationUrl) {
			//  calculate the being added urls
			const urls = await this._galleryStore.store.resolveUrls();

			runInAction(() => {
				this._newAddedProductUrls = urls;
			});

			await predictProductViolations(validProductQuery, colorQuery, presentation);
		}
	};

	/** initialize the store for products with violations */
	@action initializeViolationStore = (): void => {
		const search: Search = new Search();
		const { primaryType, futureViolationUrls, productDependencyPaths } = this;

		for (const cnl of futureViolationUrls) {
			search.setLimitUrl(cnl);
		}

		search.addDependencyPaths(productDependencyPaths);
		SearchManager.getSearchTypeSortQuery(primaryType, false, search.getSortQuery());

		this._futureViolationDataStore = new NodeDataStore(search, this.cache);
	};

	/** set the active view */
	@action setActiveView = (view: ActiveView): void => {
		this._activeView = view;
	};

	/** set the violation set of color */
	@action setFutureViolationColorUrls = (urlSet: Set<cnl>): void => {
		this._futureViolationColorUrls = urlSet;
	};

	/** set the violation set of product due to color */
	@action setFutureViolationUrlsOfProductDueToColor = (urlSet: Set<cnl>): void => {
		this._futureViolationUrlsOfProductDueToColor = urlSet;
	};

	/** set the violation set of product */
	@action setFutureViolationUrlsOfProduct = (urlSet: Set<cnl>): void => {
		this._futureViolationUrlsOfProduct = urlSet;
	};

	constructor(
		locale: ILocaleProvider,
		ac?: ControllerInterface,
		onClose?: () => void,
		onSave?: ControllerSaveFunction,
		presentationUrl?: cnl,
		runtimeRules?: RuntimeRulesSchema,
		specs?: Spec[],
		cache?: ICache,
		postUpdateRule?: VoidFunction,
	) {
		super(2, "NewPP", onClose, onSave, locale);
		makeObservable(this);

		this.actionController = ac;
		this.formData = ac?.getFormData();
		this._runtimeRules = runtimeRules;
		this.presentationUrl = presentationUrl;
		this.postUpdateRule = postUpdateRule;

		if (cache) {
			this.cache = cache;
		}

		if (specs) {
			this._specs = specs;
		}

		// initialize the rule store
		if (presentationUrl) {
			const finalSpecs = removeDuplicatedSpecs([...specs, ...this.adHocSpecs]);
			const rules = convertRuntimeRulesSchemaToCustomFilters(runtimeRules);
			this._ruleStore = new RuleStore(locale, finalSpecs, 5, SearchManager.getSpecAttributeValueStore, rules, true);
			if (!!finalSpecs.length && !rules) {
				// add one rule by default if the attribute is available
				this._ruleStore.addRule();
			}
		}

		// update gallery store when primary type changes
		this.trackReaction(reaction(() => this.primaryType, this._primaryTypeReaction));

		// update gallery store when rules change, but throttle the function so does not spawn too many queries
		this.trackReaction(reaction(() => this.productQuery?.toString(), _.throttle(this._productSearchReaction, 1000), { fireImmediately: true }));
	}
}

export const PPWizardStoreContext = createContext<PPWizardStore>(undefined);
export const usePPWizardStore = (): PPWizardStore => useContext(PPWizardStoreContext);